import { createBrowserRouter, Navigate, useLocation, type RouteObject } from 'react-router-dom';
import type { ReactElement } from 'react';
import { Roles, type Permission } from '@bmai/shared';
import AppLayout from '@/shell/AppLayout';
import OnboardingLayout from '@/shell/OnboardingLayout';
import { resolveDefaultRoute } from '@/shell/SidebarNav';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { useAuth } from '@/features/auth/AuthContext';
import OnboardingPage from '@/features/onboarding/OnboardingPage';
import LegalAcceptancePage from '@/features/legal/LegalAcceptancePage';
import AIExplorerPage from '@/features/ai-explorer/AIExplorerPage';
import InsightsPage from '@/features/insights/InsightsPage';
import GovernanceLayout from '@/features/governance/GovernanceLayout';
import GovernancePoliciesPage from '@/features/governance/GovernancePoliciesPage';
import GovernanceIssuesPage from '@/features/governance/GovernanceIssuesPage';
import GovernanceAssignedPage from '@/features/governance/GovernanceAssignedPage';
import GovernanceRiskDensityPage from '@/features/governance/GovernanceRiskDensityPage';
import GovernanceActivityPage from '@/features/governance/GovernanceActivityPage';
import PolicyDetailPage from '@/features/governance/PolicyDetailPage';
import SurfaceTopicPage from '@/shared/surfaces/SurfaceTopicPage';
import SurfaceEvidencePage from '@/shared/surfaces/SurfaceEvidencePage';
import { TOKEN_USAGE_CATALOG } from '@/features/token-usage/TokenUsageTopicCatalog';
import TokenUsageCostByDepartmentPage from '@/features/token-usage/TokenUsageCostByDepartmentPage';
import TokenUsageLayout from '@/features/token-usage/TokenUsageLayout';
import SpendTokensActivityPage from '@/features/spend/tokens/SpendTokensActivityPage';
import CostLayout from '@/features/spend/cost/CostLayout';
import SpendCostByProviderPage from '@/features/spend/cost/SpendCostByProviderPage';
import SpendCostTrendPage from '@/features/spend/cost/SpendCostTrendPage';
import SpendCostForecastPage from '@/features/spend/cost/forecast/SpendCostForecastPage';
import SpendLicenseFitPage from '@/features/spend/cost/licensefit/SpendLicenseFitPage';
import SpendCostDrillPage from '@/features/spend/cost/SpendCostDrillPage';
import ClassificationPage from '@/features/classification/ClassificationPage';
import LicensesPage from '@/features/licenses/LicensesPage';
import DirectoryPage from '@/features/directory/DirectoryPage';
import PlaceholderPage from '@/features/placeholder/PlaceholderPage';
import BehaviorLayout from '@/features/behavior/BehaviorLayout';
import BehaviorActivityPage from '@/features/behavior/BehaviorActivityPage';
import BehaviorModelFitPage from '@/features/behavior/BehaviorModelFitPage';
import BehaviorModelFitPromptsPage from '@/features/behavior/BehaviorModelFitPromptsPage';
import UseCasesPage from '@/features/behavior/UseCasesPage';
import UseCaseCategoryPage from '@/features/behavior/UseCaseCategoryPage';
import UseCaseSubcategoryPage from '@/features/behavior/UseCaseSubcategoryPage';
import UseCasePersonPage from '@/features/behavior/UseCasePersonPage';
import AdoptionLayout from '@/features/adoption/AdoptionLayout';
import AdoptionPage from '@/features/adoption/AdoptionPage';
import AdoptionDriversPage from '@/features/adoption/AdoptionDriversPage';
import SettingsLayout from '@/features/settings/SettingsLayout';
import ThemeSettingsPage from '@/features/settings/ThemeSettingsPage';
import SettingsPlaceholderPage from '@/features/settings/SettingsPlaceholderPage';
import OrganizationSettingsPage from '@/features/settings/OrganizationSettingsPage';
import DataRetentionSettingsPage from '@/features/settings/DataRetentionSettingsPage';
import SystemSettingsPage from '@/features/settings/SystemSettingsPage';
import NotificationsSettingsPage from '@/features/settings/NotificationsSettingsPage';
import BillingPage from '@/features/billing/BillingPage';
import UsageCategoriesPage from '@/features/usage-categories/UsageCategoriesPage';
import { UsersPage as AdminUsersPage } from '@/features/admin/UsersPage';
import { RolesPermissionsPage } from '@/features/admin/RolesPermissionsPage';
import { SsoPage } from '@/features/admin/SsoPage';
import IdentityProvidersPage from '@/features/integrations/IdentityProvidersPage';
import AiProvidersPage from '@/features/integrations/AiProvidersPage';
import OauthCallbackPage from '@/features/integrations/OauthCallbackPage';
import InstallCallbackPage from '@/features/integrations/InstallCallbackPage';
import SetPasswordPage from '@/features/auth/SetPasswordPage';
import VerifyEmailPage from '@/features/auth/VerifyEmailPage';
import OperatorLayout from '@/features/operator/OperatorLayout';
import OperatorLoginPage from '@/features/operator/OperatorLoginPage';
import OperatorTenantsPage from '@/features/operator/OperatorTenantsPage';
import OperatorTenantDetailPage from '@/features/operator/tenants/OperatorTenantDetailPage';
import OperatorUsersPage from '@/features/operator/users/OperatorUsersPage';
import OperatorPlansPage from '@/features/operator/plans/OperatorPlansPage';
import OperatorAuditPage from '@/features/operator/audit/OperatorAuditPage';
import OperatorBillingPage from '@/features/operator/billing/OperatorBillingPage';
import OperatorSettingsLayout from '@/features/operator/settings/OperatorSettingsLayout';
import OperatorModelSettingsPage from '@/features/operator/settings/OperatorModelSettingsPage';
import OperatorBillingIntegrationPage from '@/features/operator/settings/OperatorBillingIntegrationPage';

/** Redirects a legacy `/spend/...` link to its split destination (`/spend/tokens/*` → `/token-usage/*`, `/spend/cost/*` → `/cost/*`, bare `/spend` → `/token-usage`). */
function SpendPathRedirect(): ReactElement {
    const { pathname, search } = useLocation();
    const target =
        pathname.startsWith('/spend/cost') ? pathname.replace(/^\/spend\/cost/, '/cost')
        : pathname.startsWith('/spend/tokens') ? pathname.replace(/^\/spend\/tokens/, '/token-usage')
        : '/token-usage';
    return <Navigate to={`${target}${search}`} replace />;
}

/** The operator console's default landing: full-console admins land on Tenants, billing-only operators on Billing. */
function OperatorIndexRedirect(): ReactElement {
    const { hasRole } = useAuth();
    return <Navigate to={hasRole(Roles.PLATFORM_OPERATOR) ? '/operator/tenants' : '/operator/billing'} replace />;
}

/** Guards the admin-only operator surfaces (tenants, operators, audit): a billing-only operator is sent to Billing. */
function OperatorAdminRoute({ children }: { children: ReactElement }): ReactElement {
    const { hasRole } = useAuth();
    if (!hasRole(Roles.PLATFORM_OPERATOR)) return <Navigate to="/operator/billing" replace />;
    return children;
}

/** Landing gate: un-accepted admins hit the legal gate first, then first-run onboarding, then their default surface. */
function IndexRedirect(): ReactElement {
    const { user, legal, hasFeature, hasPermission, hasRole } = useAuth();
    if (legal?.needsAcceptance) return <Navigate to="/legal" replace />;
    if (user?.onboarding?.needsOnboarding) return <Navigate to="/onboarding" replace />;
    return <Navigate to={resolveDefaultRoute(hasFeature, hasPermission, hasRole)} replace />;
}

/** Wrap an element in a view-permission gate so direct-URL access is enforced, not just nav-hidden. */
function viewGuard(resource: Permission['resource'], element: ReactElement): ReactElement {
    return <ProtectedRoute permission={{ action: 'view', resource }}>{element}</ProtectedRoute>;
}

function surfaceRoutes(surfaceId: string, resource: Permission['resource'], gallery: ReactElement): RouteObject[] {
    return [
        { path: surfaceId, element: viewGuard(resource, gallery) },
        { path: `${surfaceId}/:topicId`, element: viewGuard(resource, <SurfaceTopicPage surfaceId={surfaceId} />) },
        { path: `${surfaceId}/:topicId/:primaryKey/:secondaryKey?`, element: viewGuard(resource, <SurfaceEvidencePage surfaceId={surfaceId} />) },
    ];
}

export const router = createBrowserRouter([
    { path: 'oauth/callback', element: <OauthCallbackPage /> },
    { path: 'integrations/install-callback', element: <InstallCallbackPage /> },
    { path: 'integrations/github-copilot/installed', element: <InstallCallbackPage /> },
    { path: 'set-password', element: <SetPasswordPage mode="invite" /> },
    { path: 'reset-password', element: <SetPasswordPage mode="reset" /> },
    { path: 'verify-email', element: <VerifyEmailPage /> },
    // Vendor operator console (SAAS-32) — its own realm/login + shell, separate
    // from the tenant AppLayout. Login/callback are public; the console is
    // role-gated inside OperatorLayout.
    { path: 'operator/login', element: <OperatorLoginPage /> },
    {
        path: 'operator',
        element: <OperatorLayout />,
        children: [
            { index: true, element: <OperatorIndexRedirect /> },
            { path: 'tenants', element: <OperatorAdminRoute><OperatorTenantsPage /></OperatorAdminRoute> },
            { path: 'tenants/:tenantId', element: <OperatorAdminRoute><OperatorTenantDetailPage /></OperatorAdminRoute> },
            { path: 'plans', element: <OperatorPlansPage /> },
            { path: 'billing', element: <OperatorBillingPage /> },
            { path: 'users', element: <OperatorAdminRoute><OperatorUsersPage /></OperatorAdminRoute> },
            { path: 'audit', element: <OperatorAdminRoute><OperatorAuditPage /></OperatorAdminRoute> },
            {
                path: 'settings',
                element: <OperatorSettingsLayout />,
                children: [
                    { index: true, element: <Navigate to="/operator/settings/models" replace /> },
                    { path: 'models', element: <OperatorModelSettingsPage /> },
                    { path: 'billing', element: <OperatorBillingIntegrationPage /> },
                ],
            },
        ],
    },
    {
        element: <OnboardingLayout />,
        children: [
            { path: 'legal', element: <ProtectedRoute role={Roles.ADMIN}><LegalAcceptancePage /></ProtectedRoute> },
            { path: 'onboarding', element: <ProtectedRoute role={Roles.ADMIN}><OnboardingPage /></ProtectedRoute> },
        ],
    },
    {
        element: <AppLayout />,
        children: [
            { index: true, element: <IndexRedirect /> },
            { path: 'insights', element: viewGuard('insights', <InsightsPage />) },
            ...surfaceRoutes('ai-explorer', 'ai_explorer', <AIExplorerPage surfaceId="ai-explorer" />),
            {
                path: 'adoption',
                element: viewGuard('adoption', <AdoptionLayout />),
                children: [
                    { index: true, element: <AdoptionPage /> },
                    { path: 'drivers', element: <AdoptionDriversPage /> },
                    { path: ':topicId', element: <SurfaceTopicPage surfaceId="adoption" /> },
                    { path: ':topicId/:primaryKey/:secondaryKey?', element: <SurfaceEvidencePage surfaceId="adoption" /> },
                ],
            },
            {
                path: 'behavior',
                element: viewGuard('behavior', <BehaviorLayout />),
                children: [
                    { index: true, element: <Navigate to="/behavior/use-cases" replace /> },
                    { path: 'use-cases', element: <UseCasesPage /> },
                    { path: 'use-cases/:category', element: <UseCaseCategoryPage /> },
                    { path: 'use-cases/:category/sub/:subcategory', element: <UseCaseSubcategoryPage /> },
                    { path: 'use-cases/:category/sub/:subcategory/:userId', element: <UseCasePersonPage /> },
                    { path: 'use-cases/:category/:userId', element: <UseCasePersonPage /> },
                    { path: 'task-mix', element: <Navigate to="/behavior/use-cases" replace /> },
                    { path: 'task-mix/*', element: <Navigate to="/behavior/use-cases" replace /> },
                    { path: 'task-detail', element: <Navigate to="/behavior/use-cases" replace /> },
                    { path: 'task-detail/*', element: <Navigate to="/behavior/use-cases" replace /> },
                    { path: 'activity', element: <BehaviorActivityPage /> },
                    { path: 'adoption', element: <Navigate to="/adoption" replace /> },
                    { path: 'adoption/drivers', element: <Navigate to="/adoption/drivers" replace /> },
                    { path: 'team-heatmap', element: <Navigate to="/adoption/team-heatmap" replace /> },
                    { path: 'adoption-trend', element: <Navigate to="/adoption/adoption-trend" replace /> },
                    { path: 'team-reach', element: <Navigate to="/adoption" replace /> },
                    { path: 'role-reach', element: <Navigate to="/adoption" replace /> },
                    { path: 'org-unit-reach', element: <Navigate to="/adoption" replace /> },
                    { path: 'user-reach', element: <Navigate to="/adoption" replace /> },
                    { path: 'model-fit', element: <BehaviorModelFitPage /> },
                    { path: 'model-fit/user/:value', element: <BehaviorModelFitPromptsPage scope="user" /> },
                    { path: 'model-fit/model/:value', element: <BehaviorModelFitPromptsPage scope="model" /> },
                    { path: ':topicId', element: <SurfaceTopicPage surfaceId="behavior" /> },
                    { path: ':topicId/:primaryKey/:secondaryKey?', element: <SurfaceEvidencePage surfaceId="behavior" /> },
                ],
            },
            {
                path: 'governance',
                element: viewGuard('governance', <GovernanceLayout />),
                children: [
                    { index: true, element: <Navigate to="/governance/policies" replace /> },
                    { path: 'policies', element: <GovernancePoliciesPage /> },
                    { path: 'issues', element: <GovernanceIssuesPage /> },
                    { path: 'assigned', element: <GovernanceAssignedPage /> },
                    { path: 'risk-density', element: <GovernanceRiskDensityPage /> },
                    { path: 'activity', element: <GovernanceActivityPage /> },
                    { path: 'policy/:policyId', element: <PolicyDetailPage /> },
                    { path: ':topicId', element: <SurfaceTopicPage surfaceId="governance" /> },
                    { path: ':topicId/:primaryKey/:secondaryKey?', element: <SurfaceEvidencePage surfaceId="governance" /> },
                ],
            },
            {
                path: 'token-usage',
                element: viewGuard('token_usage', <TokenUsageLayout />),
                children: [
                    { index: true, element: <Navigate to={`/token-usage/${TOKEN_USAGE_CATALOG[0]?.id ?? 'activity'}`} replace /> },
                    { path: 'activity', element: <SpendTokensActivityPage /> },
                    { path: ':topicId', element: <SurfaceTopicPage surfaceId="token-usage" /> },
                    { path: ':topicId/:primaryKey/:secondaryKey?', element: <SurfaceEvidencePage surfaceId="token-usage" /> },
                ],
            },
            {
                path: 'cost',
                element: viewGuard('cost', <CostLayout />),
                children: [
                    { index: true, element: <SpendCostByProviderPage /> },
                    { path: 'by-department', element: <TokenUsageCostByDepartmentPage /> },
                    { path: 'by-department/:topicId/:primaryKey/:secondaryKey?', element: <SurfaceEvidencePage surfaceId="token-usage" topicCrumb={{ label: 'By Department', to: '/cost/by-department' }} /> },
                    { path: 'forecast', element: <SpendCostForecastPage /> },
                    { path: 'license-fit', element: <SpendLicenseFitPage /> },
                    { path: 'trend', element: <SpendCostTrendPage /> },
                    { path: 'provider/:connectionId/seats', element: <SpendCostDrillPage scope="seats" /> },
                    { path: 'provider/:connectionId', element: <SpendCostDrillPage scope="provider" /> },
                    { path: 'model/:modelId', element: <SpendCostDrillPage scope="model" /> },
                ],
            },
            { path: 'spend', element: <Navigate to="/token-usage" replace /> },
            { path: 'spend/*', element: <SpendPathRedirect /> },
            { path: 'usage/*', element: <Navigate to="/behavior" replace /> },
            { path: 'usage', element: <Navigate to="/behavior" replace /> },
            { path: 'classification/:category', element: <ClassificationPage /> },
            {
                path: 'licenses',
                element: (
                    <ProtectedRoute permission={{ action: 'view', resource: 'licenses' }}>
                        <LicensesPage />
                    </ProtectedRoute>
                ),
            },
            { path: 'directory', element: <Navigate to="/settings/directory" replace /> },
            {
                path: 'settings',
                element: <SettingsLayout />,
                children: [
                    { index: true, element: <Navigate to="/settings/organization" replace /> },
                    {
                        path: 'directory',
                        element: (
                            <ProtectedRoute permission={{ action: 'view', resource: 'users' }}>
                                <DirectoryPage />
                            </ProtectedRoute>
                        ),
                    },
                    { path: 'organization', element: viewGuard('organization', <OrganizationSettingsPage />) },
                    { path: 'billing', element: viewGuard('billing', <BillingPage />) },
                    { path: 'data-retention', element: viewGuard('data_retention', <DataRetentionSettingsPage />) },
                    { path: 'system', element: viewGuard('system', <SystemSettingsPage />) },
                    { path: 'theme', element: <ThemeSettingsPage /> },
                    { path: 'notifications', element: viewGuard('notifications', <NotificationsSettingsPage />) },
                    { path: 'usage-categories', element: viewGuard('usage_categories', <UsageCategoriesPage />) },
                    { path: 'identity-providers', element: viewGuard('identity', <IdentityProvidersPage />) },
                    { path: 'providers', element: viewGuard('connections', <AiProvidersPage />) },
                    { path: 'users', element: viewGuard('users', <AdminUsersPage />) },
                    { path: 'roles', element: viewGuard('roles', <RolesPermissionsPage />) },
                    { path: 'sso', element: viewGuard('identity', <SsoPage />) },
                    { path: ':section', element: <SettingsPlaceholderPage /> },
                ],
            },
            { path: 'admin/users', element: <Navigate to="/settings/users" replace /> },
            { path: 'admin/roles', element: <Navigate to="/settings/roles" replace /> },
            { path: 'admin/sso', element: <Navigate to="/settings/sso" replace /> },
            { path: ':page', element: <PlaceholderPage /> },
        ],
    },
]);
